import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, Instagram, Facebook, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import TikTokIcon from '@/components/icons/TikTokIcon';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import { GoogleReviewCardFull } from '@/components/GoogleReviewCard';
import api from '@/utils/apiClient';

// Cafe coordinates for directions
const CAFE_COORDINATES = "-17.7633,177.4475";
const CAFE_ADDRESS = "Shop 3, Concave Road, Namaka, Nadi, Fiji";

// Generate Google Maps directions URL from current location
const getDirectionsUrl = () => {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(CAFE_ADDRESS)}&destination_place_id=&travelmode=driving`;
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    address: "Shop 3 at Concave Road, Namaka, Nadi, Fiji",
    address_extra: "",
    phone: "+679 675 0102",
    email: "administrator@bulaccinocafe.com",
    hours: "Daily: 6:30 AM - 8:00 PM",
    map_url: getDirectionsUrl(),
    instagram: "https://instagram.com/bulaccinocafe",
    facebook: "https://facebook.com/bulaccinocafe",
    tiktok: "https://www.tiktok.com/@bulaccinocafe"
  });
  
  // CMS Content for hero section
  const [cmsHero, setCmsHero] = useState({
    tagline: "We'd Love to Hear From You",
    title: "Get in Touch",
    description: "Have a question, feedback, or just want to say Bula? We'd love to hear from you!"
  });

  // Fetch contact info from settings API
  useEffect(() => {
    const fetchContactInfo = async () => {
      try {
        const res = await api.get('/contact-info');
        setContactInfo(prev => ({ ...prev, ...res.data }));
      } catch (err) {
        console.log('Using default contact info');
      }
    };
    fetchContactInfo();
    
    // Fetch CMS content for Contact page
    const fetchCMSContent = async () => {
      try {
        const res = await api.get('/simple-cms/pages/contact');
        // Parse hero section from CMS HTML
        if (res.data.content) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(res.data.content, 'text/html');
          const heroSection = doc.querySelector('.hero-section');
          if (heroSection) {
            setCmsHero({
              tagline: heroSection.querySelector('.tagline')?.textContent?.trim() || cmsHero.tagline,
              title: heroSection.querySelector('h1')?.textContent?.trim() || cmsHero.title,
              description: heroSection.querySelector('p:not(.tagline)')?.textContent?.trim() || cmsHero.description,
            });
          }
        }
      } catch (err) {
        console.log('Using default CMS content');
      }
    };
    fetchCMSContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/contact', formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    }

    setLoading(false);
  };

  // Build contact info items dynamically from settings
  const contactInfoItems = [
    {
      icon: MapPin,
      title: "Address",
      content: contactInfo.address_extra 
        ? `${contactInfo.address}\n${contactInfo.address_extra}`
        : contactInfo.address,
      link: contactInfo.map_url
    },
    {
      icon: Phone,
      title: "Phone",
      content: contactInfo.phone,
      link: `tel:${contactInfo.phone.replace(/\s/g, '')}`
    },
    {
      icon: Mail,
      title: "Email",
      content: contactInfo.email,
      link: `mailto:${contactInfo.email}`
    },
    {
      icon: Clock,
      title: "Hours",
      content: contactInfo.hours,
      link: null
    }
  ];

  return (
    <div className="pt-24 pb-16">
      <SEO {...SEO_CONFIG.contact} path="/contact" />
      
      {/* Hero */}
      <section className="py-16 bg-gradient-to-b from-secondary/50 to-transparent">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-primary font-medium mb-4"
          >
            {cmsHero.tagline}
          </motion.p>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-heading text-5xl md:text-6xl text-foreground mb-4"
          >
            {cmsHero.title}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-muted-foreground max-w-2xl mx-auto text-lg"
          >
            {cmsHero.description}
          </motion.p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-3xl mb-8">Contact Details</h2>
              
              <div className="space-y-6 mb-10">
                {contactInfoItems.map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="flex gap-4"
                    data-testid={`contact-info-${item.title.toLowerCase()}`}
                  >
                    <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{item.title}</p>
                      {item.link ? (
                        <a 
                          href={item.link} 
                          target={item.link.startsWith('http') ? '_blank' : undefined}
                          rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-muted-foreground hover:text-primary transition-colors whitespace-pre-line"
                        >
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-muted-foreground whitespace-pre-line">{item.content}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Social Links */}
              <div className="mb-10">
                <h3 className="font-medium mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  {contactInfo.instagram && (
                    <a 
                      href={contactInfo.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                      data-testid="contact-instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {contactInfo.facebook && (
                    <a 
                      href={contactInfo.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                      data-testid="contact-facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  )}
                  {contactInfo.tiktok && (
                    <a 
                      href={contactInfo.tiktok} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center hover:bg-primary hover:text-white transition-colors"
                      data-testid="contact-tiktok"
                    >
                      <TikTokIcon className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>

              {/* Google Reviews Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="mb-10"
              >
                <h3 className="font-medium mb-4">What Our Guests Say</h3>
                <GoogleReviewCardFull />
              </motion.div>

              {/* Map */}
              <div className="map-container h-72 rounded-3xl overflow-hidden">
                <iframe
                  title="Bulaccino Cafe Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3909.8!2d177.4475!3d-17.7633!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sShop+3+Concave+Road%2C+Namaka%2C+Nadi%2C+Fiji!5e0!3m2!1sen!2sfj!4v1600000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              
              {/* Get Directions Button */}
              <div className="mt-4">
                <a
                  href={getDirectionsUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-full font-medium transition-colors w-full sm:w-auto justify-center"
                  data-testid="get-directions-btn"
                >
                  <Navigation className="w-5 h-5" />
                  Get Directions to Bulaccino
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-3xl mb-8">Send a Message</h2>
              
              <motion.form 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                onSubmit={handleSubmit}
                className="bg-card p-8 rounded-3xl shadow-lg border border-border"
              >
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      className="rounded-xl"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="hello@example.com"
                      required
                      className="rounded-xl"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+679 XXX XXXX"
                      className="rounded-xl"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="How can we help you?"
                      rows={5}
                      required
                      className="rounded-xl resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full rounded-full bg-primary hover:bg-primary/90"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </motion.form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
